import { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { toast } from "sonner";
import { BarChart3, Search, FolderOpen, Terminal, Settings, Plus, Pencil, Trash2, Upload, X, Sparkles, Megaphone, Newspaper, Building2, Ticket, Radio, Handshake, Users, Building, FileSignature, Receipt, BookOpen, ShoppingCart, Image as ImageIcon, Youtube, Target, Shield } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useSeo } from "../lib/useSeo";

const tabCls = ({ isActive }) =>
  `flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`;

export function AdminLayout() {
  useSeo({ title: "Admin", noindex: true });
  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-6">Admin</h1>
        <div className="flex flex-wrap gap-2 pb-2 mb-6">
          <NavLink to="/admin" end className={tabCls} data-testid="admin-tab-analytics"><BarChart3 size={16} /> Analytics</NavLink>
          <NavLink to="/admin/searches" className={tabCls} data-testid="admin-tab-searches"><Search size={16} /> What Users Search For</NavLink>
          <NavLink to="/admin/commands" className={tabCls} data-testid="admin-tab-commands"><Terminal size={16} /> Commands</NavLink>
          <NavLink to="/admin/categories" className={tabCls} data-testid="admin-tab-categories"><FolderOpen size={16} /> Categories</NavLink>
          <NavLink to="/admin/industries" className={tabCls} data-testid="admin-tab-industries"><Building2 size={16} /> Industries</NavLink>
          <NavLink to="/admin/members" className={tabCls} data-testid="admin-tab-members"><Users size={16} /> Members</NavLink>
          <NavLink to="/admin/organisations" className={tabCls} data-testid="admin-tab-organisations"><Building size={16} /> Organisations</NavLink>
          <NavLink to="/admin/prospects" className={tabCls} data-testid="admin-tab-prospects"><Target size={16} /> Prospects</NavLink>
          <NavLink to="/admin/ndas" className={tabCls} data-testid="admin-tab-ndas"><FileSignature size={16} /> NDAs</NavLink>
          <NavLink to="/admin/invoices" className={tabCls} data-testid="admin-tab-invoices"><Receipt size={16} /> Invoices</NavLink>
          <NavLink to="/admin/abandoned" className={tabCls} data-testid="admin-tab-abandoned"><ShoppingCart size={16} /> Abandoned</NavLink>
          <NavLink to="/admin/vouchers" className={tabCls} data-testid="admin-tab-vouchers"><Ticket size={16} /> Vouchers</NavLink>
          <NavLink to="/admin/ebooks" className={tabCls} data-testid="admin-tab-ebooks"><BookOpen size={16} /> Ebooks</NavLink>
          <NavLink to="/admin/social-proof" className={tabCls} data-testid="admin-tab-social-proof"><Radio size={16} /> Signup Popups</NavLink>
          <NavLink to="/admin/broadcast" className={tabCls} data-testid="admin-tab-broadcast"><Megaphone size={16} /> Broadcast</NavLink>
          <NavLink to="/admin/blog" className={tabCls} data-testid="admin-tab-blog"><Newspaper size={16} /> Blog</NavLink>
          <NavLink to="/admin/blog-subscribers" className={tabCls} data-testid="admin-tab-blog-subscribers"><Users size={16} /> Blog subscribers</NavLink>
          <NavLink to="/admin/showcase" className={tabCls} data-testid="admin-tab-showcase"><ImageIcon size={16} /> Showcase</NavLink>
          <NavLink to="/admin/videos" className={tabCls} data-testid="admin-tab-videos"><Youtube size={16} /> Home Videos</NavLink>
          <NavLink to="/admin/partners" className={tabCls} data-testid="admin-tab-partners"><Handshake size={16} /> Partners</NavLink>
          <NavLink to="/admin/security" className={tabCls} data-testid="admin-tab-security"><Shield size={16} /> Security</NavLink>
          <NavLink to="/admin/settings" className={tabCls} data-testid="admin-tab-settings"><Settings size={16} /> Settings</NavLink>
        </div>
        <Outlet />
      </div>
    </div>
  );
}

const Sparkline = ({ data, colour = "#0E2E22" }) => {
  if (!data || data.length === 0) return null;
  const max = Math.max(1, ...data);
  const w = 72;
  const h = 20;
  const stepX = data.length > 1 ? w / (data.length - 1) : w;
  const points = data.map((v, i) => `${(i * stepX).toFixed(1)},${(h - (v / max) * (h - 2) - 1).toFixed(1)}`).join(" ");
  const last = data[data.length - 1];
  const prev = data.length >= 2 ? data[data.length - 2] : last;
  const delta = last - prev;
  const trendColour = delta > 0 ? "#059669" : delta < 0 ? "#DC2626" : "#94A3B8";
  const sum = data.reduce((a, b) => a + b, 0);
  return (
    <div className="flex items-center justify-between mt-1.5" title={`Last 7 days: ${data.join(", ")}`}>
      <svg width={w} height={h} className="overflow-visible">
        <polyline points={points} fill="none" stroke={colour} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={(data.length - 1) * stepX} cy={h - (last / max) * (h - 2) - 1} r="2" fill={colour} />
      </svg>
      <span className="text-[10px] font-semibold" style={{ color: trendColour }}>
        {sum > 0 ? `${delta >= 0 ? "+" : ""}${delta} today · 7d: ${sum}` : "7d: 0"}
      </span>
    </div>
  );
};

const Stat = ({ label, value, testId, sub, trend }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={testId}>
    <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</p>
    <p className="font-display text-2xl font-extrabold mt-1.5">{value}</p>
    {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
    {trend && <Sparkline data={trend} />}
  </div>
);

export function AdminAnalytics() {
  const [d, setD] = useState(null);
  const [trial, setTrial] = useState(null);
  const [recon, setRecon] = useState(null);
  const [period, setPeriod] = useState("all");
  useEffect(() => {
    setD(null);
    api.get(`/admin/analytics?period=${period}`).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
  }, [period]);
  useEffect(() => {
    api.get("/admin/trial-stats").then((r) => setTrial(r.data)).catch(() => {});
    api.get("/admin/trial-reconciliation").then((r) => setRecon(r.data)).catch(() => {});
  }, []);
  const periods = [
    { v: "7d",  label: "Last 7 days" },
    { v: "30d", label: "Last 30 days" },
    { v: "all", label: "All time" },
  ];
  const Toggle = (
    <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 gap-1" data-testid="admin-period-toggle">
      {periods.map((p) => (
        <button key={p.v} type="button"
          onClick={() => setPeriod(p.v)}
          data-testid={`period-${p.v}`}
          className={`text-xs sm:text-sm font-bold px-3 py-1.5 rounded-lg transition-colors ${period === p.v ? "bg-[#0E2E22] text-[#D4AF37]" : "text-slate-600 hover:bg-slate-100"}`}>
          {p.label}
        </button>
      ))}
    </div>
  );
  if (!d) return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-bold">Analytics</h2>
        {Toggle}
      </div>
      <p className="text-slate-400">Loading…</p>
    </div>
  );
  const rev = (d.revenue_pence / 100).toFixed(2);
  const t = d.trends || {};
  const revTrend = (t.revenue_pence || []).map((p) => p / 100);
  const totalUsers = d.total_users || 0;
  const unverifiedPct = totalUsers > 0 ? Math.round((d.registered_unverified / totalUsers) * 100) : 0;
  const noTrialPct = totalUsers > 0 ? Math.round((d.verified_no_trial / totalUsers) * 100) : 0;
  const periodLabel = periods.find((p) => p.v === d.range)?.label || "All time";
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-bold">Analytics</h2>
          <p className="text-xs text-slate-500">Showing <span className="font-semibold text-slate-700">{periodLabel}</span> · sparklines always show the last 7 daily buckets</p>
        </div>
        {Toggle}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        <Stat label="Landing visitors" value={d.landing_visitors} testId="stat-landing" trend={t.landing_visitors} sub={typeof d.landing_visitors_unique_total === 'number' ? `Unique humans so far: ${d.landing_visitors_unique_total.toLocaleString()}` : null} />
        <Stat label="Pricing visits" value={d.pricing_visits} testId="stat-pricing" trend={t.pricing_visits} sub={typeof d.pricing_visits_unique_total === 'number' ? `Unique humans so far: ${d.pricing_visits_unique_total.toLocaleString()}` : null} />
        <Stat label="Checkout starts" value={d.checkout_starts} testId="stat-checkout-starts" trend={t.checkout_starts} />
        <Stat label="Checkout completions" value={d.checkout_completions} testId="stat-completions" trend={t.checkout_completions} />
        <Stat label="Conversion rate" value={`${d.conversion_rate}%`} testId="stat-conversion" />
        <Stat label="Total members" value={d.total_members} testId="stat-members" />
        <Stat label="Revenue" value={`£${rev}`} testId="stat-revenue" trend={revTrend} />
        <Stat label="Search volume" value={d.search_volume} testId="stat-searches" />
        <Stat label="24hr trials started" value={d.trials_started || 0} testId="stat-trials" trend={t.trials_started} sub="excludes test accounts" />
        <Stat label="£4.99 intro purchases" value={d.purchases?.pro_intro || 0} testId="stat-intro-purchases" />
        <Stat label="Student purchases" value={d.purchases?.student_annual || 0} sub={d.manual_purchases?.student_annual ? `incl. ${d.manual_purchases.student_annual} manual` : null} testId="stat-student-purchases" />
        <Stat label="Pro purchases" value={d.purchases?.pro_annual || 0} sub={d.manual_purchases?.pro_annual ? `incl. ${d.manual_purchases.pro_annual} manual` : null} testId="stat-pro-purchases" />
        <Stat label="Lifetime purchases" value={d.purchases?.lifetime || 0} sub={d.manual_purchases?.lifetime ? `incl. ${d.manual_purchases.lifetime} manual` : null} testId="stat-lifetime-purchases" />
        <Stat label="Total accounts" value={d.total_users} testId="stat-users" trend={t.signups} />
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="admin-signup-dropoff">
        <div className="flex items-baseline justify-between mb-3">
          <p className="font-display font-bold text-sm">Signup drop-off diagnostic</p>
          <p className="text-xs text-slate-400">Where real sign-ups leak before they reach a trial</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-xl bg-rose-50 border border-rose-200 p-4" data-testid="dropoff-unverified">
            <div className="text-[11px] uppercase tracking-wider text-rose-700 font-bold">Registered, not verified</div>
            <div className="text-2xl font-display font-bold text-rose-800 mt-1">{d.registered_unverified}</div>
            <div className="text-[11px] text-rose-700 mt-0.5">{unverifiedPct}% of all accounts · check email delivery</div>
            {d.registered_unverified > 0 && (
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/dropoff/export?group=unverified`}
                 data-testid="export-dropoff-unverified"
                 className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-rose-700 hover:text-rose-900 underline underline-offset-2">
                Export CSV
              </a>
            )}
          </div>
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-4" data-testid="dropoff-no-trial">
            <div className="text-[11px] uppercase tracking-wider text-amber-700 font-bold">Verified, no trial claimed</div>
            <div className="text-2xl font-display font-bold text-amber-800 mt-1">{d.verified_no_trial}</div>
            <div className="text-[11px] text-amber-700 mt-0.5">{noTrialPct}% of all accounts · nudge with reminder email</div>
            {d.verified_no_trial > 0 && (
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/dropoff/export?group=no_trial`}
                 data-testid="export-dropoff-no-trial"
                 className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-amber-700 hover:text-amber-900 underline underline-offset-2">
                Export CSV
              </a>
            )}
          </div>
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4" data-testid="dropoff-trials">
            <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-bold">Trials claimed</div>
            <div className="text-2xl font-display font-bold text-emerald-800 mt-1">{d.trials_started || 0}</div>
            <div className="text-[11px] text-emerald-700 mt-0.5">Of {totalUsers} accounts · activation rate {totalUsers ? Math.round((d.trials_started || 0) / totalUsers * 100) : 0}%</div>
          </div>
        </div>
        <p className="text-[11px] text-slate-500 mt-3">
          Auto-nudges run daily at 10:00 London time — one verification reminder and one trial nudge per person (Day 2+). The trial nudge includes a one-click magic link that auto-starts the 24-hour pass.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Who signed up recently:</span>
          {[7, 14, 28].map((n) => (
            <a key={n}
               href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/accounts/export?days=${n}`}
               data-testid={`export-accounts-${n}d`}
               className="inline-flex items-center gap-1.5 text-[11px] font-bold text-slate-700 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-full px-3 py-1.5 transition-colors">
              Last {n} days CSV
            </a>
          ))}
          <span className="text-[11px] text-slate-400 ml-auto">Includes email, name, verification + trial + payment status</span>
        </div>
      </div>

      {trial && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="admin-trial-funnel">
          <div className="flex items-baseline justify-between mb-3">
            <p className="font-display font-bold text-sm">24-hour trial funnel</p>
            <p className="text-xs text-slate-400">Card-less trial only</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
              <div className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">Started</div>
              <div className="text-xl font-display font-bold text-[#0B0F0D]" data-testid="trial-stat-started">{trial.started}</div>
            </div>
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3">
              <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-bold">Active now</div>
              <div className="text-xl font-display font-bold text-emerald-800" data-testid="trial-stat-active">{trial.active}</div>
            </div>
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
              <div className="text-[11px] uppercase tracking-wider text-amber-700 font-bold">Expired (unpaid)</div>
              <div className="text-xl font-display font-bold text-amber-800" data-testid="trial-stat-expired">{trial.expired}</div>
            </div>
            <div className="rounded-xl bg-[#0B0F0D] border border-[#D4AF37]/40 p-3">
              <div className="text-[11px] uppercase tracking-wider text-[#D4AF37] font-bold">Converted</div>
              <div className="text-xl font-display font-bold text-white" data-testid="trial-stat-converted">{trial.converted}</div>
            </div>
            <div className="rounded-xl bg-white border border-slate-200 p-3">
              <div className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">Conv. rate</div>
              <div className="text-xl font-display font-bold text-[#0B0F0D]" data-testid="trial-stat-rate">{trial.conversion_rate}%</div>
            </div>
          </div>
        </div>
      )}

      {recon && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="admin-trial-reconciliation">
          <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
            <div>
              <p className="font-display font-bold text-sm">Trial reconciliation</p>
              <p className="text-xs text-slate-500">Dashboard vs raw DB vs event stream — proves the headline number is honest</p>
            </div>
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
              Bot filter does NOT touch trial events
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Users collection (raw)</div>
              <div className="mt-2 space-y-0.5 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">trial_used=True</span><span className="font-bold" data-testid="recon-users-total">{recon.users_truth.trial_used_total}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">with started_at</span><span>{recon.users_truth.with_trial_started_at}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">missing started_at</span><span className={recon.users_truth.missing_trial_started_at > 0 ? "text-amber-700 font-semibold" : ""}>{recon.users_truth.missing_trial_started_at}</span></div>
                <div className="flex justify-between pt-1 border-t border-slate-200 mt-1"><span className="text-slate-500">active · converted · expired</span><span>{recon.users_truth.active} · {recon.users_truth.converted} · {recon.users_truth.expired}</span></div>
              </div>
            </div>

            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4">
              <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-bold">Real users (test accounts excluded)</div>
              <div className="mt-2 space-y-0.5 text-sm">
                <div className="flex justify-between"><span className="text-emerald-700">Trials claimed</span><span className="font-bold text-emerald-900" data-testid="recon-real-total">{recon.real_truth.trial_used_total}</span></div>
                <div className="flex justify-between"><span className="text-emerald-700">Active now</span><span>{recon.real_truth.active}</span></div>
                <div className="flex justify-between"><span className="text-emerald-700">Converted to paid</span><span>{recon.real_truth.converted}</span></div>
                <div className="flex justify-between"><span className="text-emerald-700">Expired unpaid</span><span>{recon.real_truth.expired}</span></div>
              </div>
            </div>

            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Event stream (trial_start)</div>
              <div className="mt-2 space-y-0.5 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Total events logged</span><span className="font-bold" data-testid="recon-events-total">{recon.events_truth.total_trial_start_events}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Distinct user IDs</span><span>{recon.events_truth.distinct_user_ids}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Orphaned (user deleted)</span><span className={recon.events_truth.orphan_user_ids > 0 ? "text-amber-700 font-semibold" : ""}>{recon.events_truth.orphan_user_ids}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Bot-UA events</span><span className={recon.events_truth.bot_ua_events > 0 ? "text-rose-700 font-semibold" : "text-emerald-700"}>{recon.events_truth.bot_ua_events}</span></div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <div className={`rounded-xl p-3 border ${recon.deltas.dashboard_minus_real === 0 ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-amber-50 border-amber-200 text-amber-900"}`}>
              <div className="text-[11px] uppercase tracking-wider font-bold opacity-70">Dashboard − Real</div>
              <div className="text-lg font-display font-bold" data-testid="recon-delta-dashboard">
                {recon.deltas.dashboard_minus_real === 0 ? "In sync ✓" : `+${recon.deltas.dashboard_minus_real} test account${recon.deltas.dashboard_minus_real === 1 ? "" : "s"} auto-excluded`}
              </div>
            </div>
            <div className={`rounded-xl p-3 border ${recon.deltas.events_minus_users === 0 ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-slate-50 border-slate-200 text-slate-700"}`}>
              <div className="text-[11px] uppercase tracking-wider font-bold opacity-70">Events − Users</div>
              <div className="text-lg font-display font-bold" data-testid="recon-delta-events">
                {recon.deltas.events_minus_users === 0 ? "In sync ✓" : `${recon.deltas.events_minus_users > 0 ? "+" : ""}${recon.deltas.events_minus_users} orphan event${Math.abs(recon.deltas.events_minus_users) === 1 ? "" : "s"} (deleted users)`}
              </div>
            </div>
          </div>

          {recon.test_accounts_included_in_raw?.sample?.length > 0 && (
            <details className="mt-3 group" data-testid="recon-test-sample">
              <summary className="cursor-pointer text-[11px] font-bold uppercase tracking-wider text-slate-500 hover:text-slate-700">
                {recon.test_accounts_included_in_raw.count} test account{recon.test_accounts_included_in_raw.count === 1 ? "" : "s"} filtered out — show emails
              </summary>
              <ul className="mt-2 space-y-1 text-xs text-slate-600 font-mono">
                {recon.test_accounts_included_in_raw.sample.map((s) => (
                  <li key={s.email} className="flex flex-wrap gap-x-3">
                    <span className="text-slate-800">{s.email}</span>
                    <span className="text-slate-400">· {s.membership || "free"}</span>
                    {s.trial_started_at && <span className="text-slate-400">· started {s.trial_started_at.slice(0, 10)}</span>}
                  </li>
                ))}
              </ul>
            </details>
          )}

          <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">{recon.note}</p>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4">
        {[["Most copied", d.most_copied], ["Most viewed", d.most_viewed], ["Most saved", d.most_saved]].map(([title, rows]) => (
          <div key={title} className="bg-white border border-slate-200 rounded-2xl p-5">
            <p className="font-display font-bold text-sm mb-3">{title}</p>
            {(!rows || rows.length === 0) && <p className="text-xs text-slate-400">No data yet</p>}
            {(rows || []).map((r) => (
              <div key={r._id} className="flex justify-between text-sm py-1.5 border-b border-slate-50 last:border-0">
                <span className="cmd-chip font-semibold text-slate-700">/{r._id}</span><span className="text-slate-400">{r.n}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminSecurity() {
  const [d, setD] = useState(null);
  const [usage, setUsage] = useState(null);
  const loadUsage = () => api.get("/admin/security/usage").then((r) => setUsage(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/admin/security/probes?limit=100")
      .then((r) => setD(r.data))
      .catch((e) => toast.error(apiError(e)));
    loadUsage();
  }, []);
  const unpause = async (uid, email) => {
    if (!window.confirm(`Unpause ${email}?`)) return;
    try {
      await api.post(`/admin/security/unpause/${uid}`);
      toast.success(`Unpaused ${email}`);
      loadUsage();
    } catch (e) { toast.error(apiError(e)); }
  };
  if (!d) return <p className="text-slate-400">Loading security probes…</p>;
  const section = (title, rows, labelFn) => (
    <div className="bg-white border border-slate-200 rounded-2xl p-4">
      <p className="font-display font-bold text-sm mb-2">{title}</p>
      {(!rows || rows.length === 0) && <p className="text-xs text-slate-400">No hits in the last 7 days — quiet is good.</p>}
      <ul className="text-xs space-y-1">
        {(rows || []).map((r, i) => (
          <li key={i} className="flex justify-between gap-2 py-1 border-b border-slate-50 last:border-0">
            <span className="font-mono text-slate-700 break-all">{labelFn ? labelFn(r.value) : r.value}</span>
            <span className="text-slate-500 shrink-0">{r.n}</span>
          </li>
        ))}
      </ul>
    </div>
  );
  return (
    <div className="space-y-6" data-testid="admin-security">
      <div>
        <h2 className="font-display text-xl font-bold">Honeypot probe log</h2>
        <p className="text-xs text-slate-500">Any hit to a scanner-only path (<span className="font-mono">/wp-login</span>, <span className="font-mono">/.env</span>, <span className="font-mono">/phpmyadmin</span>, etc.) is logged here and the attacker gets a plain 404.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="rounded-2xl bg-[#0B0F0D] border border-[#D4AF37]/40 p-5 text-white">
          <div className="text-[11px] uppercase tracking-wider text-[#D4AF37] font-bold">Probes last 7 days</div>
          <div className="text-3xl font-display font-bold mt-1" data-testid="probes-total-7d">{d.total_7d}</div>
          <div className="text-[11px] text-white/70 mt-0.5">across {d.distinct_ips_7d} distinct IP{d.distinct_ips_7d === 1 ? "" : "s"}</div>
        </div>
        <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5">
          <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-bold">What this means</div>
          <p className="text-xs text-emerald-900 mt-1 leading-relaxed">
            Scanners that bounce off these paths get a 404 with no clue the probe was logged. Spikes from a single IP or UA mean someone's actively mapping the site — safe to firewall at the CDN.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {section("Top paths probed", d.top_paths_7d)}
        {section("Top IPs", d.top_ips_7d)}
        {section("Top user agents", d.top_user_agents_7d, (v) => (v || "").slice(0, 80))}
      </div>

      {/* ------ Account-sharing enforcement ------ */}
      {usage && (
        <div className="pt-4 border-t border-slate-200" data-testid="admin-account-sharing">
          <div className="flex items-end justify-between flex-wrap gap-2 mb-3">
            <div>
              <h2 className="font-display text-xl font-bold">Account-sharing enforcement</h2>
              <p className="text-xs text-slate-500">
                Live limits: {usage.limits.per_minute}/min · {usage.limits.per_hour}/hr · {usage.limits.per_day}/day · tripwire &gt; {usage.limits.unique_warn_per_hour} unique/hr (warn), &gt; {usage.limits.unique_pause_per_day} unique/day (auto-pause).
              </p>
            </div>
          </div>

          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 mb-4">
            <p className="font-display font-bold text-sm text-rose-900 mb-2">
              Accounts paused for review ({usage.paused_accounts.length})
            </p>
            {usage.paused_accounts.length === 0 && (
              <p className="text-xs text-rose-700/80">None right now — nobody's over the daily unique-command ceiling.</p>
            )}
            <ul className="divide-y divide-rose-100">
              {usage.paused_accounts.map((p) => (
                <li key={p.id} className="py-2 flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-mono text-sm text-rose-950 break-all">{p.email}</div>
                    <div className="text-[11px] text-rose-700">
                      {p.membership || "free"} · paused {(p.paused_at || "").slice(0, 19).replace("T", " ")} · {p.unique_24h_at_pause} unique commands in 24h
                    </div>
                  </div>
                  <button
                    data-testid={`unpause-${p.id}`}
                    onClick={() => unpause(p.id, p.email)}
                    className="px-3 py-1.5 rounded-full bg-rose-900 text-white text-xs font-bold hover:bg-rose-800"
                  >
                    Unpause
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-white border border-slate-200 rounded-2xl p-4">
              <p className="font-display font-bold text-sm mb-2">Top usage · unique commands last 24h</p>
              {usage.top_unique_commands_24h.length === 0 && <p className="text-xs text-slate-400">No consumption-signal events yet.</p>}
              <ul className="text-xs divide-y divide-slate-50">
                {usage.top_unique_commands_24h.map((r) => (
                  <li key={r.user_id} className="py-1.5 flex justify-between gap-2">
                    <span className="font-mono text-slate-800 break-all">{r.email}</span>
                    <span className={r.unique_commands > usage.limits.unique_warn_per_hour ? "text-rose-700 font-bold" : "text-slate-500"}>{r.unique_commands}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-4">
              <p className="font-display font-bold text-sm mb-2">Top usage · API hits last 24h</p>
              {usage.top_api_hits_24h.length === 0 && <p className="text-xs text-slate-400">Quiet.</p>}
              <ul className="text-xs divide-y divide-slate-50">
                {usage.top_api_hits_24h.map((r) => (
                  <li key={r.user_id} className="py-1.5 flex justify-between gap-2">
                    <span className="font-mono text-slate-800 break-all">{r.email}</span>
                    <span className={r.hits_24h > usage.limits.per_day ? "text-rose-700 font-bold" : "text-slate-500"}>{r.hits_24h}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl p-4">
        <p className="font-display font-bold text-sm mb-2">Last 100 honeypot hits</p>
        {d.recent.length === 0 && <p className="text-xs text-slate-400">Nothing yet.</p>}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-slate-500">
              <tr className="text-left">
                <th className="py-1.5 pr-3">When</th>
                <th className="py-1.5 pr-3">Path</th>
                <th className="py-1.5 pr-3">IP</th>
                <th className="py-1.5 pr-3">UA</th>
                <th className="py-1.5 pr-3">Source</th>
              </tr>
            </thead>
            <tbody>
              {d.recent.map((r) => (
                <tr key={r._id} className="border-t border-slate-100">
                  <td className="py-1.5 pr-3 text-slate-500 whitespace-nowrap">{(r.created_at || "").slice(0, 19).replace("T", " ")}</td>
                  <td className="py-1.5 pr-3 font-mono text-slate-800 break-all">{r.path}</td>
                  <td className="py-1.5 pr-3 font-mono text-slate-700">{r.ip}</td>
                  <td className="py-1.5 pr-3 text-slate-500 max-w-xs truncate" title={r.ua}>{r.ua || "—"}</td>
                  <td className="py-1.5 pr-3 text-slate-500">{r.source === "backend" ? "backend" : "spa-404"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function AdminSearches() {
  const [d, setD] = useState(null);
  const [categories, setCategories] = useState([]);
  const [draft, setDraft] = useState(null);
  const [drafting, setDrafting] = useState(null);

  const load = () => api.get("/admin/search-analytics").then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); api.get("/categories").then((r) => setCategories(r.data)); }, []);

  const draftCommand = async (phrase) => {
    setDrafting(phrase);
    try {
      const { data } = await api.post("/admin/draft-command", { phrase });
      setDraft({ ...data, active: true, order: 0 });
    } catch (e) { toast.error(apiError(e)); }
    setDrafting(null);
  };

  const saveDraft = async (payload) => {
    await api.post("/admin/commands", payload);
    toast.success(`/${payload.slug} added to the dictionary`);
    load();
  };

  if (!d) return <p className="text-slate-400">Loading…</p>;
  return (
    <div className="grid md:grid-cols-2 gap-5">
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <p className="font-display font-bold mb-1">What users are searching for</p>
        <p className="text-xs text-slate-400 mb-4">Top phrases, whether results were found, and which command was clicked.</p>
        {d.top_searches.length === 0 && <p className="text-sm text-slate-400">No searches yet.</p>}
        <div className="space-y-1.5" data-testid="top-searches-list">
          {d.top_searches.map((r) => (
            <div key={r._id} className="flex items-center justify-between text-sm py-2 border-b border-slate-50 last:border-0 gap-2">
              <div className="min-w-0">
                <span className="font-medium text-slate-800">{r._id}</span>
                {r.clicked?.length > 0 && <span className="text-xs text-emerald-600 ml-2">→ /{r.clicked[0]}</span>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${r.found ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-500"}`}>{r.found ? "found" : "no results"}</span>
                <span className="text-slate-400 text-xs">{r.searches}×</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <p className="font-display font-bold mb-1 text-red-500">Searches with no results</p>
        <p className="text-xs text-slate-400 mb-4">Tap “Draft with AI” to turn real demand into a new command instantly.</p>
        {d.no_results.length === 0 && <p className="text-sm text-slate-400">Every search found results. 🎉</p>}
        <div className="space-y-1.5" data-testid="no-results-list">
          {d.no_results.map((r) => (
            <div key={r._id} className="flex items-center justify-between text-sm py-2 border-b border-slate-50 last:border-0 gap-2">
              <span className="font-medium text-slate-800 min-w-0 truncate">{r._id}</span>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-slate-400 text-xs">{r.searches}×</span>
                <button data-testid={`draft-ai-btn-${r._id.replace(/\s+/g, "-")}`} onClick={() => draftCommand(r._id)} disabled={drafting !== null}
                  className="flex items-center gap-1 bg-slate-900 text-amber-400 text-xs font-bold px-2.5 py-1.5 rounded-lg hover:bg-slate-800 disabled:opacity-50 transition-all">
                  <Sparkles size={12} /> {drafting === r._id ? "Drafting…" : "Draft with AI"}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      {draft && <CommandForm initial={draft} categories={categories} onSave={saveDraft} onClose={() => setDraft(null)} />}
    </div>
  );
}

const emptyCmd = { slug: "", category: "", examples: ["", "", ""], keywords: [], synonyms: [], related: [], access: "all", active: true, order: 0, applies_to_all_industries: true, industries: [] };
const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40";

function CommandForm({ initial, categories, onSave, onClose }) {
  const [f, setF] = useState({ ...emptyCmd, ...initial, examples: [...(initial?.examples || ["", "", ""])] });
  const [industryList, setIndustryList] = useState([]);
  useEffect(() => { api.get("/industries").then((r) => setIndustryList(r.data)).catch(() => {}); }, []);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const listVal = (arr) => (Array.isArray(arr) ? arr.join(", ") : arr);
  const parseList = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

  const indMap = Object.fromEntries((f.industries || []).map((m) => [m.industry_slug, m]));
  const toggleInd = (slug) => {
    const next = { ...indMap };
    if (next[slug]) delete next[slug];
    else next[slug] = { industry_slug: slug, relevance: 75 };
    set("industries", Object.values(next));
  };
  const setRel = (slug, rel) => {
    set("industries", (f.industries || []).map((m) => (m.industry_slug === slug ? { ...m, relevance: Number(rel) } : m)));
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...f, examples: f.examples.filter(Boolean), order: Number(f.order) || 0 };
    if (payload.examples.length < 3) { toast.error("Please provide at least 3 examples"); return; }
    try {
      await onSave(payload);
      onClose();
    } catch (e2) { toast.error(apiError(e2)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl p-6 w-full max-w-xl my-8 space-y-4" data-testid="command-form-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">{initial?.id ? "Edit command" : "Add command"}</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs font-semibold text-slate-500">Command (slug)</label>
            <input data-testid="cmd-form-slug" className={inputCls} value={f.slug} onChange={(e) => set("slug", e.target.value)} placeholder="facebook-ad" required /></div>
          <div><label className="text-xs font-semibold text-slate-500">Category</label>
            <select data-testid="cmd-form-category" className={inputCls} value={f.category} onChange={(e) => set("category", e.target.value)} required>
              <option value="">Choose…</option>
              {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select></div>
        </div>
        <div><label className="text-xs font-semibold text-slate-500">Examples (min 3)</label>
          <div className="space-y-2">
            {f.examples.map((ex, i) => (
              <input key={i} data-testid={`cmd-form-example-${i}`} className={inputCls} value={ex} placeholder={`Example ${i + 1}`}
                onChange={(e) => { const c = [...f.examples]; c[i] = e.target.value; set("examples", c); }} />
            ))}
            <button type="button" onClick={() => set("examples", [...f.examples, ""])} className="text-xs text-emerald-700 font-semibold">+ Add another example</button>
          </div></div>
        <div><label className="text-xs font-semibold text-slate-500">Keywords (comma-separated)</label>
          <input data-testid="cmd-form-keywords" className={inputCls} value={listVal(f.keywords)} onChange={(e) => set("keywords", parseList(e.target.value))} /></div>
        <div><label className="text-xs font-semibold text-slate-500">Synonyms / related phrases (comma-separated)</label>
          <input data-testid="cmd-form-synonyms" className={inputCls} value={listVal(f.synonyms)} onChange={(e) => set("synonyms", parseList(e.target.value))} /></div>
        <div><label className="text-xs font-semibold text-slate-500">Related commands (comma-separated slugs)</label>
          <input data-testid="cmd-form-related" className={inputCls} value={listVal(f.related)} onChange={(e) => set("related", parseList(e.target.value))} /></div>
        <div className="grid grid-cols-3 gap-3">
          <div><label className="text-xs font-semibold text-slate-500">Access</label>
            <select data-testid="cmd-form-access" className={inputCls} value={f.access} onChange={(e) => set("access", e.target.value)}>
              <option value="all">All members</option>
              <option value="student">Student</option>
              <option value="pro">Pro</option>
            </select></div>
          <div><label className="text-xs font-semibold text-slate-500">Display order</label>
            <input data-testid="cmd-form-order" type="number" className={inputCls} value={f.order} onChange={(e) => set("order", e.target.value)} /></div>
          <div className="flex items-end pb-2">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input data-testid="cmd-form-active" type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} className="w-4 h-4 accent-emerald-600" /> Active
            </label></div>
        </div>
        <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-100">
          <label className="flex items-center gap-2 text-sm font-semibold">
            <input data-testid="cmd-form-universal" type="checkbox" checked={f.applies_to_all_industries}
              onChange={(e) => set("applies_to_all_industries", e.target.checked)} className="w-4 h-4 accent-emerald-600" />
            Universal — applies to all industries
          </label>
          {!f.applies_to_all_industries && (
            <div className="mt-3 max-h-48 overflow-y-auto space-y-1.5" data-testid="cmd-form-industries-list">
              {industryList.map((ind) => (
                <div key={ind.slug} className="flex items-center justify-between gap-2">
                  <label className="flex items-center gap-2 text-xs font-medium min-w-0 cursor-pointer">
                    <input type="checkbox" checked={!!indMap[ind.slug]} onChange={() => toggleInd(ind.slug)} className="w-3.5 h-3.5 accent-emerald-600 shrink-0" />
                    <span className="truncate">{ind.name}</span>
                  </label>
                  {indMap[ind.slug] && (
                    <select value={indMap[ind.slug].relevance} onChange={(e) => setRel(ind.slug, e.target.value)}
                      className="text-xs bg-white border border-slate-200 rounded-lg px-1.5 py-1 shrink-0">
                      <option value={100}>100 · specific</option>
                      <option value={75}>75 · strong</option>
                      <option value={50}>50 · general</option>
                    </select>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <button data-testid="cmd-form-save-btn" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 rounded-xl transition-all">Save command</button>
      </form>
    </div>
  );
}

export function AdminCommands() {
  const [cmds, setCmds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/commands", { params: { q, category: cat } }).then((r) => setCmds(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, cat]); // eslint-disable-line
  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)); }, []);

  const save = async (payload) => {
    if (editing?.id) await api.put(`/admin/commands/${editing.id}`, payload);
    else await api.post("/admin/commands", payload);
    toast.success("Saved");
    load();
  };

  const del = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    await api.delete(`/admin/commands/${c.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-5">
        <input data-testid="admin-cmd-search" className={`${inputCls} max-w-xs`} placeholder="Search commands…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select data-testid="admin-cmd-category-filter" className={`${inputCls} max-w-[220px]`} value={cat} onChange={(e) => setCat(e.target.value)}>
          <option value="">All categories</option>
          {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
        <button data-testid="admin-add-command-btn" onClick={() => setEditing({})}
          className="ml-auto bg-slate-900 text-white text-sm font-bold px-4 py-2.5 rounded-xl flex items-center gap-1.5 hover:bg-slate-800 transition-all"><Plus size={15} /> Add command</button>
      </div>
      <p className="text-sm text-slate-400 mb-3">{cmds.length} commands</p>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr><th className="px-4 py-3">Command</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Access</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Order</th><th className="px-4 py-3"></th></tr>
            </thead>
            <tbody>
              {cmds.map((c) => (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                  <td className="px-4 py-2.5 cmd-chip font-semibold text-slate-800">{c.name}</td>
                  <td className="px-4 py-2.5 text-slate-500">{c.category}</td>
                  <td className="px-4 py-2.5"><span className="text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 uppercase">{c.access}</span></td>
                  <td className="px-4 py-2.5">{c.active ? <span className="text-emerald-600 text-xs font-bold">Active</span> : <span className="text-slate-400 text-xs font-bold">Inactive</span>}</td>
                  <td className="px-4 py-2.5 text-slate-400">{c.order}</td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <button data-testid={`admin-edit-cmd-${c.slug}`} onClick={() => setEditing(c)} className="text-slate-400 hover:text-emerald-600 p-1.5"><Pencil size={15} /></button>
                    <button data-testid={`admin-delete-cmd-${c.slug}`} onClick={() => del(c)} className="text-slate-400 hover:text-red-500 p-1.5"><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {editing !== null && <CommandForm initial={editing} categories={categories} onSave={save} onClose={() => setEditing(null)} />}
    </div>
  );
}

export function AdminCategories() {
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const [access, setAccess] = useState("all");
  const load = () => api.get("/categories").then((r) => setCats(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/categories", { name, access, order: cats.length });
      setName(""); toast.success("Category created"); load();
    } catch (e2) { toast.error(apiError(e2)); }
  };

  const rename = async (c) => {
    const newName = window.prompt("Category name:", c.name);
    if (!newName || newName === c.name) return;
    await api.put(`/admin/categories/${c.id}`, { name: newName, access: c.access, order: 0, disclaimer: c.disclaimer || "" });
    toast.success("Renamed"); load();
  };

  const setCatAccess = async (c, newAccess) => {
    await api.put(`/admin/categories/${c.id}`, { name: c.name, access: newAccess, order: 0, disclaimer: c.disclaimer || "" });
    toast.success("Updated"); load();
  };

  const editDisclaimer = async (c) => {
    const next = window.prompt(
      `Disclaimer shown above the ${c.name} grid (leave blank to remove):`,
      c.disclaimer || ""
    );
    if (next === null) return;
    await api.put(`/admin/categories/${c.id}`, { name: c.name, access: c.access, order: 0, disclaimer: next });
    toast.success(next ? "Disclaimer saved" : "Disclaimer removed");
    load();
  };

  const del = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    try {
      await api.delete(`/admin/categories/${c.id}`);
      toast.success("Deleted"); load();
    } catch (e2) { toast.error(apiError(e2)); }
  };

  return (
    <div className="max-w-2xl">
      <form onSubmit={add} className="flex flex-wrap gap-3 mb-6">
        <input data-testid="admin-new-category-input" className={`${inputCls} flex-1 min-w-[200px]`} placeholder="New category name" value={name} onChange={(e) => setName(e.target.value)} required />
        <select data-testid="admin-new-category-access" className={`${inputCls} w-40`} value={access} onChange={(e) => setAccess(e.target.value)}>
          <option value="all">All members</option><option value="student">Student</option><option value="pro">Pro</option>
        </select>
        <button data-testid="admin-add-category-btn" className="bg-slate-900 text-white text-sm font-bold px-4 py-2.5 rounded-xl hover:bg-slate-800 transition-all">Add</button>
      </form>
      <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
        {cats.map((c) => (
          <div key={c.id} className="flex items-center justify-between px-5 py-3.5 gap-3">
            <div>
              <p className="font-semibold text-sm">{c.name}</p>
              <p className="text-xs text-slate-400">{c.count} commands{c.disclaimer ? " · ⚠ disclaimer set" : ""}</p>
            </div>
            <div className="flex items-center gap-2">
              <select data-testid={`admin-category-access-${c.slug}`} value={c.access} onChange={(e) => setCatAccess(c, e.target.value)}
                className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5">
                <option value="all">All members</option><option value="student">Student</option><option value="pro">Pro</option>
              </select>
              <button data-testid={`admin-disclaimer-${c.slug}`} onClick={() => editDisclaimer(c)}
                className={`text-xs font-semibold px-2 py-1 rounded-lg ${c.disclaimer ? "bg-amber-100 text-amber-900 hover:bg-amber-200" : "text-slate-400 hover:text-amber-700 hover:bg-amber-50"}`}
                title="Edit category disclaimer">⚠</button>
              <button data-testid={`admin-rename-category-${c.slug}`} onClick={() => rename(c)} className="text-slate-400 hover:text-emerald-600 p-1.5"><Pencil size={15} /></button>
              <button data-testid={`admin-delete-category-${c.slug}`} onClick={() => del(c)} className="text-slate-400 hover:text-red-500 p-1.5"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminBroadcast() {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [recent, setRecent] = useState([]);
  const [selected, setSelected] = useState([]);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [audience, setAudience] = useState("members");
  const [counts, setCounts] = useState({ members: 0, enterprise_all: 0, enterprise_new: 0, enterprise_contacted: 0 });

  const loadHistory = () => api.get("/admin/broadcasts").then((r) => setHistory(r.data)).catch(() => {});
  const loadCounts = () => api.get("/admin/broadcast/audience-counts").then((r) => setCounts(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/admin/commands").then((r) => {
      const sorted = [...r.data].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setRecent(sorted.slice(0, 20));
    });
    loadHistory();
    loadCounts();
  }, []);

  const toggle = (slug) => setSelected((s) => (s.includes(slug) ? s.filter((x) => x !== slug) : [...s, slug]));

  const AUDIENCE_OPTIONS = [
    { key: "members", label: "Paid members", desc: "Student, Pro & Lifetime", count: counts.members },
    { key: "enterprise_new", label: "Enterprise — new enquiries", desc: "Haven't been contacted yet", count: counts.enterprise_new },
    { key: "enterprise_contacted", label: "Enterprise — contacted", desc: "Follow-ups to active conversations", count: counts.enterprise_contacted },
    { key: "enterprise_all", label: "Enterprise — all enquiries", desc: "Every company that submitted the form", count: counts.enterprise_all },
  ];
  const currentCount = AUDIENCE_OPTIONS.find((a) => a.key === audience)?.count || 0;
  const audienceLabel = AUDIENCE_OPTIONS.find((a) => a.key === audience)?.label || "members";

  const send = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Send this email to ${currentCount} ${audienceLabel.toLowerCase()}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/broadcast", { subject, message, command_slugs: selected, audience });
      toast.success(data.message);
      setSubject(""); setMessage(""); setSelected([]);
      loadHistory();
    } catch (e2) { toast.error(apiError(e2)); }
    setBusy(false);
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6 max-w-5xl">
      <form onSubmit={send} className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
        <div>
          <p className="font-display font-bold">New broadcast email</p>
          <p className="text-xs text-slate-400 mt-1">Email paid members about new commands, or follow up with Enterprise form submissions.</p>
        </div>

        <div data-testid="broadcast-audience-block">
          <label className="text-xs font-semibold text-slate-500">Who should receive this?</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1.5">
            {AUDIENCE_OPTIONS.map((o) => (
              <label key={o.key} data-testid={`broadcast-audience-${o.key}`}
                className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-all ${audience === o.key ? "border-emerald-500 bg-emerald-50/70" : "border-slate-200 hover:border-slate-300"}`}>
                <input type="radio" name="audience" value={o.key} checked={audience === o.key}
                  onChange={() => setAudience(o.key)} className="mt-0.5 accent-emerald-600" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-800 flex items-center gap-2">
                    {o.label}
                    <span className="text-[10px] font-black uppercase bg-slate-900 text-amber-300 rounded-full px-2 py-0.5">{o.count}</span>
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5">{o.desc}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div><label className="text-xs font-semibold text-slate-500">Subject (optional)</label>
          <input data-testid="broadcast-subject-input" className={inputCls} value={subject} onChange={(e) => setSubject(e.target.value)}
            placeholder={audience === "members" ? "New commands added — Universal Short-Command Prompt Dictionary™" : "Following up on your enterprise enquiry"} /></div>
        <div><label className="text-xs font-semibold text-slate-500">Message</label>
          <textarea data-testid="broadcast-message-input" className={`${inputCls} min-h-[90px]`} value={message} onChange={(e) => setMessage(e.target.value)}
            placeholder={audience === "members" ? "We've just added a batch of new commands to help you…" : "Hi — just checking in on your enterprise enquiry. We'd love to set up a quick call…"} /></div>
        {audience === "members" && (
          <div>
            <label className="text-xs font-semibold text-slate-500">Include commands ({selected.length} selected — 20 most recently added shown)</label>
            <div className="mt-1.5 max-h-56 overflow-y-auto border border-slate-200 rounded-xl divide-y divide-slate-50" data-testid="broadcast-command-list">
              {recent.map((c) => (
                <label key={c.id} className="flex items-center gap-2.5 px-3.5 py-2 text-sm cursor-pointer hover:bg-slate-50">
                  <input type="checkbox" data-testid={`broadcast-cmd-${c.slug}`} checked={selected.includes(c.slug)} onChange={() => toggle(c.slug)} className="w-4 h-4 accent-emerald-600" />
                  <span className="cmd-chip font-semibold text-slate-800">{c.name}</span>
                  <span className="text-xs text-slate-400 truncate">{c.category}</span>
                </label>
              ))}
            </div>
          </div>
        )}
        <button data-testid="broadcast-send-btn" disabled={busy || currentCount === 0}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 rounded-xl transition-all disabled:opacity-60 flex items-center justify-center gap-2">
          <Megaphone size={16} /> {busy ? "Queuing…" : `Send to ${currentCount} ${audienceLabel.toLowerCase()}`}
        </button>
      </form>
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <p className="font-display font-bold mb-3">Past broadcasts</p>
        {history.length === 0 && <p className="text-sm text-slate-400">No broadcasts sent yet.</p>}
        <div className="space-y-3" data-testid="broadcast-history">
          {history.map((b) => (
            <div key={b.id} className="border border-slate-100 rounded-xl p-3.5">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-slate-800 truncate">{b.subject || "New commands added"}</p>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full shrink-0 ${b.status === "sent" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-600"}`}>
                  {b.status === "sent" ? `sent to ${b.sent_count}` : "sending…"}
                </span>
              </div>
              <p className="text-[10px] font-bold uppercase text-slate-400 mt-1 tracking-wider">
                {b.audience?.startsWith("enterprise") ? `Enterprise — ${b.audience.replace("enterprise_","")}` : "Paid members"}
              </p>
              {b.command_slugs?.length > 0 && (
                <p className="text-xs text-slate-400 mt-1.5 truncate">{b.command_slugs.map((s) => `/${s}`).join("  ")}</p>
              )}
              <p className="text-[11px] text-slate-300 mt-1">{(b.created_at || "").slice(0, 16).replace("T", " ")}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function AdminSettings() {
  const [label, setLabel] = useState("");
  const [file, setFile] = useState(null);
  const [pinging, setPinging] = useState(false);
  const [pingResult, setPingResult] = useState(null);
  useEffect(() => { api.get("/meta").then((r) => setLabel(r.data.command_count_label)); }, []);

  const saveLabel = async (e) => {
    e.preventDefault();
    await api.put("/admin/settings", { command_count_label: label });
    toast.success("Saved — the public command count has been updated");
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/admin/cheatsheet", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Cheat sheet replaced");
      setFile(null);
    } catch (e2) { toast.error(apiError(e2)); }
  };

  const downloadSpec = async () => {
    try {
      const r = await api.get("/admin/specification", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "AI-Prompt-Shortcuts-Full-Specification.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e2) { toast.error(apiError(e2)); }
  };

  const pingIndexNow = async () => {
    setPinging(true);
    setPingResult(null);
    try {
      const r = await api.post("/admin/indexnow/ping");
      setPingResult(r.data);
      const submitted = r.data?.submitted || 0;
      toast.success(`Submitted ${submitted} URLs to Bing / Yandex / Naver`);
    } catch (e2) {
      toast.error(apiError(e2));
    } finally {
      setPinging(false);
    }
  };

  return (
    <div className="max-w-lg space-y-6">
      <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="admin-spec-card">
        <p className="font-display font-bold mb-1">Full application specification</p>
        <p className="text-xs text-slate-400 mb-4">Confidential PDF describing every feature, plan, integration and API of this platform. Only visible to you, here in the admin area.</p>
        <button onClick={downloadSpec} data-testid="admin-spec-download-btn"
          className="bg-emerald-700 text-white text-sm font-bold px-5 py-2.5 rounded-xl flex items-center gap-1.5 hover:bg-emerald-800 transition-all">
          Download specification (PDF)
        </button>
      </div>
      <form onSubmit={saveLabel} className="bg-white border border-slate-200 rounded-2xl p-6">
        <p className="font-display font-bold mb-1">Displayed command count</p>
        <p className="text-xs text-slate-400 mb-4">Shown on the landing page (e.g. "hundreds of"). Change it if you ever want a specific number again.</p>
        <div className="flex gap-3">
          <input data-testid="admin-count-label-input" className={inputCls} value={label} onChange={(e) => setLabel(e.target.value)} />
          <button data-testid="admin-count-label-save" className="bg-emerald-600 text-white text-sm font-bold px-5 rounded-xl hover:bg-emerald-700 transition-all">Save</button>
        </div>
      </form>
      <form onSubmit={upload} className="bg-white border border-slate-200 rounded-2xl p-6">
        <p className="font-display font-bold mb-1">Replace cheat sheet PDF</p>
        <p className="text-xs text-slate-400 mb-4">Members download this from their member area. Upload a new PDF to replace it.</p>
        <input data-testid="admin-cheatsheet-file" type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files[0])}
          className="text-sm mb-4 block w-full" />
        <button data-testid="admin-cheatsheet-upload-btn" disabled={!file}
          className="bg-slate-900 text-white text-sm font-bold px-5 py-2.5 rounded-xl flex items-center gap-1.5 disabled:opacity-50 hover:bg-slate-800 transition-all">
          <Upload size={15} /> Upload &amp; replace
        </button>
      </form>
      <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="admin-indexnow-card">
        <p className="font-display font-bold mb-1">Search engine push (IndexNow)</p>
        <p className="text-xs text-slate-400 mb-4">
          Push every public page — home, pricing, blog posts, industries and all commands — to Bing, Yandex and Naver in one call. Bing powers ChatGPT search, so this is the fastest way to get new content cited.
        </p>
        <button onClick={pingIndexNow} disabled={pinging} data-testid="admin-indexnow-btn"
          className="bg-emerald-700 text-white text-sm font-bold px-5 py-2.5 rounded-xl flex items-center gap-1.5 hover:bg-emerald-800 disabled:opacity-60 transition-all">
          {pinging ? "Submitting…" : "Submit all URLs to Bing now"}
        </button>
        {pingResult && (
          <div className="mt-4 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="admin-indexnow-result">
            <p className="font-semibold text-slate-800">Submitted {pingResult.submitted} URLs.</p>
            <ul className="mt-2 space-y-0.5">
              {Object.entries(pingResult.results || {}).map(([ep, code]) => (
                <li key={ep}><span className="font-mono">{ep.replace("https://", "")}</span> → <span className={String(code).startsWith("2") ? "text-emerald-700 font-semibold" : "text-amber-700 font-semibold"}>{String(code)}</span></li>
              ))}
            </ul>
            <p className="mt-2 text-[11px] text-slate-500">Response codes: 200/202 = accepted. 422 = domain not yet verified in Bing.</p>
          </div>
        )}
      </div>
    </div>
  );
}
